import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { OAuthCredentialTypes } from '~src/connectivity/serverApi/types/OAuth/IOAuthCredential';

import { fetchOAuthClients } from '../oauth-clients-registry/OAuthClientsRegistry';
import { PROVIDER_TYPES } from './GenerativeAITypes';

// Fallback config id used when the onPremLLMOAuthConfigId site setting is unset.
export const HARDCODED_LLM_GATEWAY_OAUTH_CONFIG_ID = 'custom_llmgateway_config';

export interface AnalyticsAssistantFetchOptions {
  /** The currently configured LLM provider, e.g. PROVIDER_TYPES.BRING_YOUR_OWN_LLM. */
  provider?: string;
  /** Called when the response is a 401 (native or envelope) and BYOLLM OAuth is configured. */
  onAuthRequired?: () => void;
}

// Auth failures arrive either as a native 401 or as HTTP 200 with `{ httpCode: 401 }` in the body.
async function isUnauthorizedResponse(response: Response): Promise<boolean> {
  if (response.status === 401) {
    return true;
  }
  if (!response.ok) {
    return false;
  }
  try {
    const cloned = response.clone();
    const text = await cloned.text();
    const data = JSON.parse(text);
    return data?.httpCode === 401;
  } catch {
    return false;
  }
}

/**
 * Wraps `fetch` for analytics-assistant endpoints. When the response is a 401
 * (native or 200 envelope) AND BYOLLM OAuth is configured, invokes
 * `options.onAuthRequired` so the caller can prompt sign-in. The original
 * response is always returned unchanged — this helper does not retry.
 */
export async function analyticsAssistantFetchWithOAuthRetry(
  url: string,
  init: RequestInit,
  options: AnalyticsAssistantFetchOptions = {}
): Promise<Response> {
  const response = await fetch(url, init);

  if (!(await isUnauthorizedResponse(response))) {
    return response;
  }
  if (!FeatureFlags.analyticsAssistantBYOLLMOAuthEnabled) {
    return response;
  }
  if (options.provider !== PROVIDER_TYPES.BRING_YOUR_OWN_LLM) {
    return response;
  }

  // Confirm an llm-gateway client exists; otherwise there's nothing to sign in to.
  let hasLlmGatewayClient = false;
  try {
    const result = await fetchOAuthClients();
    hasLlmGatewayClient = !!result.oauthClients?.some(
      (c) => c.type === OAuthCredentialTypes.LlmGateway
    );
  } catch {
    return response;
  }

  if (!hasLlmGatewayClient) {
    return response;
  }

  options.onAuthRequired?.();
  return response;
}
